import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatAxiosError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Pencil, ShieldCheck } from "lucide-react";

const ROLES = ["lecteur", "commercial", "manager", "admin", "owner"];
const emptyUser = { name: "", email: "", password: "", roles: {}, is_superadmin: false };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUser);

  const load = () => Promise.all([
    api.get("/admin/users").then((r) => setUsers(r.data)),
    api.get("/admin/companies").then((r) => setCompanies(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyUser); setOpen(true); };
  const openEdit = (u) => { setEditing(u.id); setForm({ name: u.name, email: u.email, password: "", roles: { ...u.roles }, is_superadmin: u.is_superadmin }); setOpen(true); };

  const setRole = (slug, role) => {
    const roles = { ...form.roles };
    if (role === "none") delete roles[slug]; else roles[slug] = role;
    setForm({ ...form, roles });
  };

  const submit = async () => {
    if (!form.name.trim() || (!editing && (!form.email.trim() || !form.password))) return toast.error("Champs requis manquants");
    try {
      if (editing) {
        await api.put(`/admin/users/${editing}`, { name: form.name, password: form.password || undefined, roles: form.roles, is_superadmin: form.is_superadmin });
        toast.success("Utilisateur mis à jour");
      } else {
        await api.post("/admin/users", form);
        toast.success("Utilisateur créé");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatAxiosError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatAxiosError(e)); }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Administration</p>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Utilisateurs</h1>
        </div>
        <Button onClick={openNew} data-testid="add-user-btn" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold h-11">
          <Plus className="w-4 h-4 mr-1" /> Nouvel utilisateur
        </Button>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-border text-xs tracking-[0.1em] uppercase font-semibold text-muted-foreground">
          <div className="col-span-4">Utilisateur</div>
          <div className="col-span-5">Rôles</div>
          <div className="col-span-2">Statut</div>
          <div className="col-span-1"></div>
        </div>
        {users.map((u) => (
          <div key={u.id} data-testid={`user-${u.id}`} className="grid grid-cols-12 items-center px-6 py-4 border-b border-border last:border-0 gap-2">
            <div className="col-span-12 md:col-span-4">
              <p className="font-medium">{u.name}</p>
              <p className="text-sm text-muted-foreground">{u.email}</p>
            </div>
            <div className="col-span-8 md:col-span-5 flex flex-wrap gap-1">
              {Object.entries(u.roles || {}).map(([s, r]) => (
                <span key={s} className="text-[11px] px-2 py-0.5 rounded-sm bg-muted text-muted-foreground font-medium">{s}: {r}</span>
              ))}
              {Object.keys(u.roles || {}).length === 0 && <span className="text-xs text-muted-foreground">—</span>}
            </div>
            <div className="col-span-3 md:col-span-2">
              {u.is_superadmin && <span className="text-[11px] px-2 py-0.5 rounded-sm bg-primary/10 text-primary font-medium">Super-admin</span>}
            </div>
            <div className="col-span-1 flex justify-end gap-1">
              <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`} className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className="mt-1.5 rounded-sm" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" className="mt-1.5 rounded-sm" /></div>
            <div><Label>{editing ? "Nouveau mot de passe (optionnel)" : "Mot de passe *"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" className="mt-1.5 rounded-sm" /></div>

            <div>
              <Label className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Accès & rôles par société</Label>
              <div className="mt-2 space-y-2 border border-border rounded-sm p-3">
                {companies.map((c) => (
                  <div key={c.slug} className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">{c.name}</span>
                    <Select value={form.roles[c.slug] || "none"} onValueChange={(v) => setRole(c.slug, v)}>
                      <SelectTrigger className="w-40 rounded-sm h-9" data-testid={`role-${c.slug}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun accès</SelectItem>
                        {ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_superadmin} onChange={(e) => setForm({ ...form, is_superadmin: e.target.checked })} data-testid="user-superadmin" className="w-4 h-4 accent-[hsl(var(--primary))]" />
              Super-administrateur (accès total, gestion des sociétés & utilisateurs)
            </label>
          </div>
          <DialogFooter>
            <Button onClick={submit} data-testid="save-user-btn" className="rounded-sm bg-primary hover:bg-primary/90">{editing ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
